import { PageLoading } from '@ant-design/pro-components';
import { ListOfferBase } from '@features/products/components/formProduct/listOfferBase';
import { useFormProduct } from '@features/products/hooks/useFormProduct';
import {
  buildMonthlyOffer,
  hasOfferAnyFilledField,
  MONTHLY_ACQUISITION_ID,
  MONTHLY_SEASONALITY_ID,
} from '@features/products/utils/offerBase';
import { ModalAlert } from '@molecules';
import {
  Controller,
  InputNumberController,
  PageHeader,
  SelectController,
  SwitchController,
} from '@organisms';
import { Button, Col, Divider, Form, Modal, Row } from 'antd';
import { useMemo } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';

export const FormProduct = () => {
  const {
    control,
    handleSubmit,
    currentProductData,
    productId,
    handleCreateProduct,
    handleErrorsProduct,
    titlePage,
    isViewingMode,
    handleBack,
    isFormValid,
    isErrorProduct,
    profilesData,
    providersData,
    hasLoadingPage,
    isLoading,
    showProductLoadErrorModal,
    handleProductLoadErrorConfirm,
    offerProperties,
    watch,
    hasExistingOffers,
    setValue,
    rulesTASRelational,
    categoriesProductData,
  } = useFormProduct();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'offers',
  });

  // Watch offers values in real-time for duplicate detection
  const offersValues = watch('offers') || [];
  const isMonthly = watch('isMonthly') || false;
  const isCreateMode = !productId && !currentProductData && !isViewingMode;

  const applyMonthlyMode = () => {
    const fallbackOfferMonthly = [buildMonthlyOffer(offersValues[0])];

    replace(fallbackOfferMonthly);
    setValue('offers', fallbackOfferMonthly, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('isMonthly', true, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleMonthlyChange = (
    checked: boolean,
    field: ControllerRenderProps<any, string>
  ) => {
    if (!isCreateMode) {
      field.onChange(checked);
      return;
    }

    if (!checked) {
      field.onChange(false);
      return;
    }

    const extraOffers = offersValues.slice(1);

    if (extraOffers.length === 0) {
      applyMonthlyMode();
      return;
    }

    if (extraOffers.every(offer => !hasOfferAnyFilledField(offer))) {
      applyMonthlyMode();
      return;
    }

    Modal.confirm({
      title: 'Confirmar alteração',
      content:
        'Deseja realmente alterar para mensal? As ofertas cadastradas serão resetadas!',
      okText: 'Sim',
      cancelText: 'Não',
      maskClosable: false,
      onOk: applyMonthlyMode,
      onCancel: () => field.onChange(false),
    });
  };

  const subtitlePage = useMemo(() => {
    if (isViewingMode) return `Ver produto.`;

    return productId ? `Editar produto.` : 'Cadastre um novo Produto.';
  }, [productId, currentProductData, isViewingMode]);

  const providerDefaultValue = useMemo(() => {
    return currentProductData?.providerId &&
      providersData?.find(
        provider => provider.id === currentProductData.providerId
      )
      ? String(currentProductData.providerId)
      : isViewingMode
        ? `${String(currentProductData?.providerId || '')}`
        : undefined;
  }, [currentProductData, providersData, isViewingMode]);

  if (hasLoadingPage) {
    return <PageLoading />;
  }

  if (productId && isErrorProduct) {
    return (
      <ModalAlert
        showModal={showProductLoadErrorModal}
        confirmButtonLabel='Voltar'
        confirmButtonAction={handleProductLoadErrorConfirm}
        denyButtonAction={handleProductLoadErrorConfirm}
        hideDenyButton
        hideCloseButtonHeader
        isCentered
        titleLabel='Erro ao carregar produto'
        messageLabel='Não foi possível carregar o produto. Entre em contato com o suporte.'
      />
    );
  }

  if (productId && !currentProductData) return null;

  return (
    <>
      <PageHeader title={titlePage} subTitle={subtitlePage} />
      <Form
        layout='vertical'
        onFinish={handleSubmit(handleCreateProduct, handleErrorsProduct)}
        requiredMark={false}
      >
        <Row style={{ display: 'flex', width: '100%', gap: '24px' }}>
          <Col style={{ width: '100%' }}>
            <Controller
              control={control}
              showCount={true}
              name={`name`}
              defaultValue={currentProductData?.publicName || ''}
              placeholder='Digite o nome do produto'
              autoComplete='off'
              label='Nome do Produto'
              maxLength={120}
              disabled={isViewingMode}
              required
            />
          </Col>
          <Col style={{ width: '100%' }}>
            <SelectController
              label={'Profile de liberação'}
              required
              options={
                profilesData?.map(profile => ({
                  label: `${profile.id} - ${profile.name}`,
                  value: String(profile.id),
                })) || []
              }
              control={control}
              defaultValue={
                currentProductData?.profileId
                  ? String(currentProductData.profileId)
                  : undefined
              }
              name={'releaseProfileId'}
              placeholder='Selecione um profile de liberação'
              disabled={!!currentProductData}
              allowClear
              showSearch
            />
          </Col>
          <Col style={{ width: '100%' }}>
            <SelectController
              label={'Categorias do Produto'}
              required
              options={
                categoriesProductData?.map(category => ({
                  label: `${category.id} - ${category.name}`,
                  value: String(category.id),
                })) || []
              }
              control={control}
              defaultValue={
                currentProductData?.categories &&
                currentProductData.categories.length > 0
                  ? currentProductData.categories.map(categoryId =>
                      String(categoryId)
                    )
                  : undefined
              }
              name={'categories'}
              disabled={isViewingMode}
              placeholder='Selecione pelo menos uma categoria'
              allowClear
              showSearch
              mode='multiple'
            />
          </Col>
          <Col style={{ width: '100%' }}>
            <InputNumberController
              control={control}
              name={`warrantyDays`}
              defaultValue={currentProductData?.warrantyDays || undefined}
              placeholder='Ex.: 30'
              autoComplete='off'
              style={{ width: '100%' }}
              label='Garantia em dias'
              required
              disabled={isViewingMode}
            />
          </Col>
          <Col style={{ width: '100%' }}>
            <SelectController
              label={'Adquirente'}
              required
              options={
                providersData?.map(provider => ({
                  label: provider.name,
                  value: String(provider.id),
                })) || []
              }
              control={control}
              defaultValue={providerDefaultValue}
              name={'providerId'}
              placeholder='Selecione um adquirente'
              disabled={isViewingMode}
            />
          </Col>
        </Row>

        {offerProperties && (!currentProductData || hasExistingOffers) && (
          <ListOfferBase
            fields={fields}
            control={control}
            setValue={setValue}
            offerProperties={offerProperties}
            rulesTASRelational={rulesTASRelational}
            isMonthly={isCreateMode && isMonthly}
            handleAddSectionOffer={() =>
              append({
                fullPrice: '',
                acquisitionId: isMonthly ? MONTHLY_ACQUISITION_ID : '',
                seasonalityId: isMonthly ? MONTHLY_SEASONALITY_ID : '',
                typeId: '',
                isFallbackOffer: fields.length === 0,
              })
            }
            handleRemoveSectionOffer={index => remove(index)}
            offersValues={offersValues}
            isReadOnly={!!currentProductData}
            showAddButton={!currentProductData}
            showRemoveButton={!currentProductData}
          />
        )}

        <Row style={{ display: 'flex', width: '100%', gap: '24px' }}>
          <Col style={{ width: '100%' }}>
            <SwitchController
              label='Apenas cobrança recorrente na primeira compra'
              control={control}
              name='isOnlyFallback'
              tooltip='Cobra o valor mensal no primeiro ciclo da aquisição (Recorrente). E o valor cheio ao renovar.'
              disabled={!!currentProductData}
              defaultValue={currentProductData?.isDirectFallback || false}
              horizontal
            />
          </Col>
          <Col style={{ width: '100%' }}>
            <SwitchController
              label='Produto mensal'
              control={control}
              name='isMonthly'
              onChange={handleMonthlyChange}
              tooltip='Só pode ser ofertado/acessado por 1 mês.'
              disabled={!!currentProductData}
              defaultValue={
                currentProductData?.subscriptionType === 'monthly' || false
              }
              horizontal
            />
          </Col>
          <Col style={{ width: '100%' }}>
            <SwitchController
              label='Produto principal'
              control={control}
              name='isPrincipal'
              tooltip='Produto principal é aquele que garante acesso completo aos conteúdos de Treinos.'
              disabled={!!currentProductData}
              defaultValue={currentProductData?.isPrincipal || false}
              horizontal
            />
          </Col>
        </Row>

        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '0 32px 24px 76px',
            marginTop: '24px',
            background: '#141414',
          }}
        >
          <Row justify='end' style={{ columnGap: '12px' }}>
            <Divider style={{ margin: '0 0 24px' }} />
            <Button
              style={{
                borderRadius: '6px',
                paddingLeft: '16px',
                paddingRight: '16px',
              }}
              disabled={isLoading}
              onClick={handleBack}
            >
              Cancelar
            </Button>
            {!isViewingMode && (
              <Button
                type='primary'
                htmlType='submit'
                loading={isLoading}
                disabled={!isFormValid || isLoading}
                style={{
                  borderRadius: '6px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                }}
              >
                {currentProductData ? 'Salvar alterações' : 'Criar produto'}
              </Button>
            )}
          </Row>
        </div>
      </Form>
    </>
  );
};
